<template>
	<div class="metricBrowserRoot" role="dialog" aria-labelledby="metricBrowserTitle">
		<div id="metricBrowserTitle" class="title">Browse Metrics</div>
		<div class="searchRow">
			<input ref="searchInput"
				   type="text"
				   class="searchInput"
				   v-model="searchText"
				   placeholder="Search metric names…"
				   aria-label="Search metric names"
				   @input="onSearchInput" />
		</div>
		<div class="searchHint" v-if="!searchActive">
			Type to search{{deepSearchSupported ? " all levels of" : ""}} the metric tree, or expand branches below.
			Wildcard patterns such as <code class="inline">stats.gauges.*.perf.*</code> are supported.
		</div>
		<div class="browserBody">
			<div v-if="searchActive">
				<div v-if="searching" class="loading"><ScaleLoader /> Searching…</div>
				<div v-else-if="searchError" class="error">{{searchError}}</div>
				<div v-else-if="!searchResults.length" class="noResults">No metrics matched.</div>
				<div v-else>
					<div v-for="r in searchResults" :key="r.id"
						 :class="{ metricRow: true, selected: r.id === selectedPath }">
						<span class="rowIcon" aria-hidden="true">{{r.leaf ? "▪" : "▸"}}</span>
						<a role="button" tabindex="0" class="rowName"
						   @click="pickResult(r)"
						   @keydown.enter.space.prevent="pickResult(r)"
						   @dblclick="pickAndAccept(r)">{{r.id}}</a>
						<a v-if="!r.leaf" role="button" tabindex="0" class="drillLink" title="List children of this branch"
						   @click="drillInto(r)"
						   @keydown.enter.space.prevent="drillInto(r)">expand »</a>
					</div>
					<div v-if="searchTruncated" class="noResults">Result list truncated. Refine your search.</div>
				</div>
			</div>
			<div v-else>
				<div v-if="treeError" class="error">{{treeError}}</div>
				<div v-else-if="!treeRoot.children" class="loading"><ScaleLoader /> Loading…</div>
				<div v-else>
					<div v-for="row in visibleTreeRows" :key="row.node.id"
						 :class="{ metricRow: true, selected: row.node.id === selectedPath }"
						 :style="{ paddingLeft: (row.depth * 22) + 'px' }">
						<a v-if="!row.node.leaf" role="button" tabindex="0" class="expander"
						   :aria-label="(row.node.expanded ? 'Collapse ' : 'Expand ') + row.node.id"
						   :aria-expanded="row.node.expanded ? 'true' : 'false'"
						   @click="toggleNode(row.node)"
						   @keydown.enter.space.prevent="toggleNode(row.node)">{{row.node.expanded ? "▼" : "▶"}}</a>
						<span v-else class="rowIcon" aria-hidden="true">▪</span>
						<a role="button" tabindex="0" class="rowName"
						   @click="pickNode(row.node)"
						   @keydown.enter.space.prevent="pickNode(row.node)"
						   @dblclick="pickAndAccept(row.node)">{{row.node.text}}</a>
						<span v-if="row.node.loading" class="nodeLoading">loading…</span>
					</div>
				</div>
			</div>
		</div>
		<div class="pathRow">
			<label class="pathLabel" for="metricBrowserPath">Path:</label>
			<input id="metricBrowserPath" type="text" class="pathInput" v-model="selectedPath"
				   placeholder="stats.gauges.…" />
			<input type="button" value="Test" title="Count metrics matching this path" @click="testPath" />
			<span class="matchInfo" role="status">{{matchInfo}}</span>
		</div>
		<div v-if="templateMode" class="templateRow">
			<label><input type="checkbox" v-model="substituteServerName" />
				Replace the server name with <code class="inline">%%ServerName%%</code> when inserting</label>
		</div>
		<div class="buttonRow">
			<input type="button" value="Use This Path" :disabled="!selectedPath" @click="accept" />
			<input type="button" value="Cancel" @click="DefaultClose" />
		</div>
	</div>
</template>

<script>
	import { GraphiteFindMetrics, GraphiteSupportsDeepSearch } from 'appRoot/api/GraphiteMetadata';

	export default {
		props:
		{
			initialPath: {
				type: String,
				default: ""
			},
			/** True when editing a MultiServer template graph, offering %%ServerName%% substitution. */
			templateMode: {
				type: Boolean,
				default: false
			},
			serverNames: {
				type: Array,
				default: () => []
			}
		},
		data()
		{
			return {
				searchText: "",
				searchActive: false,
				searching: false,
				searchError: null,
				searchResults: [],
				searchTruncated: false,
				searchTimeout: null,
				searchCounter: 0,
				deepSearchSupported: false,
				treeRoot: { id: "", text: "", leaf: false, expanded: true, loading: false, children: null },
				treeError: null,
				selectedPath: "",
				matchInfo: "",
				substituteServerName: true
			};
		},
		created()
		{
			this.selectedPath = this.browseablePath(this.initialPath);
			GraphiteSupportsDeepSearch().then(supported => { this.deepSearchSupported = supported; });
			this.loadChildren(this.treeRoot)
				.then(() =>
				{
					if (this.selectedPath)
						this.expandToPath(this.selectedPath);
				})
				.catch(err => { this.treeError = err.message; });
		},
		beforeUnmount()
		{
			if (this.searchTimeout)
				clearTimeout(this.searchTimeout);
		},
		computed:
		{
			visibleTreeRows()
			{
				let rows = [];
				let visit = (node, depth) =>
				{
					if (!node.children)
						return;
					for (let child of node.children)
					{
						rows.push({ node: child, depth });
						if (child.expanded)
							visit(child, depth + 1);
					}
				};
				visit(this.treeRoot, 0);
				return rows;
			}
		},
		methods:
		{
			SetFocus()
			{
				if (this.$refs.searchInput)
					this.$refs.searchInput.focus();
			},
			DefaultClose()
			{
				this.$emit("close", false);
			},
			accept()
			{
				if (!this.selectedPath)
					return;
				this.$emit("close", { path: this.templatize(this.selectedPath) });
			},
			/** Substitutes the preview server into %%ServerName%% so a template path can be browsed against real metrics. */
			browseablePath(path)
			{
				if (path && this.serverNames.length)
					return path.replace(/%%ServerName%%/gi, this.serverNames[0]);
				return path || "";
			},
			/** Converts a concrete path back into template form if requested. */
			templatize(path)
			{
				if (!this.templateMode || !this.substituteServerName)
					return path;
				let segments = path.split(".");
				for (let i = 0; i < segments.length; i++)
					for (let sn of this.serverNames)
						if (sn && segments[i].toLowerCase() === sn.toLowerCase())
							segments[i] = "%%ServerName%%";
				return segments.join(".");
			},
			loadChildren(node)
			{
				node.loading = true;
				let query = node.id ? node.id + ".*" : "*";
				return GraphiteFindMetrics(query)
					.then(results =>
					{
						node.children = results.map(r => ({
							id: r.id, text: r.text, leaf: !!r.leaf, expanded: false, loading: false, children: null
						}));
					})
					.finally(() => { node.loading = false; });
			},
			toggleNode(node)
			{
				node.expanded = !node.expanded;
				if (node.expanded && !node.children && !node.loading)
					this.loadChildren(node).catch(err => { toaster.error(err.message); });
			},
			pickNode(node)
			{
				this.selectedPath = node.id;
				this.matchInfo = "";
				if (!node.leaf)
					this.toggleNode(node);
			},
			pickResult(r)
			{
				this.selectedPath = r.id;
				this.matchInfo = "";
			},
			pickAndAccept(nodeOrResult)
			{
				this.selectedPath = nodeOrResult.id;
				if (nodeOrResult.leaf)
					this.accept();
			},
			drillInto(r)
			{
				this.searchText = r.id + ".*";
				this.runSearch();
			},
			/** Expands tree branches down to the given path so the initial selection is visible. */
			expandToPath(path)
			{
				let segments = path.split(".");
				let expandNext = (node, index) =>
				{
					if (index >= segments.length || !node.children)
						return;
					let seg = segments[index];
					let child = node.children.find(c => c.text === seg);
					if (!child)
						return;
					if (!child.leaf && index < segments.length - 1)
					{
						child.expanded = true;
						if (!child.children)
							this.loadChildren(child).then(() => expandNext(child, index + 1)).catch(() => { });
						else
							expandNext(child, index + 1);
					}
				};
				expandNext(this.treeRoot, 0);
			},
			onSearchInput()
			{
				if (this.searchTimeout)
					clearTimeout(this.searchTimeout);
				this.searchTimeout = setTimeout(() => { this.runSearch(); }, 400);
			},
			runSearch()
			{
				let text = this.searchText.trim();
				if (!text)
				{
					this.searchActive = false;
					this.searching = false;
					return;
				}
				this.searchActive = true;
				this.searching = true;
				this.searchError = null;
				let counter = ++this.searchCounter;
				let hasWildcard = /[*?{[]/.test(text) || text.indexOf(".") !== -1;
				let queries;
				if (hasWildcard)
					queries = [text];
				else if (this.deepSearchSupported)
					queries = ["*" + text + "*", "**.*" + text + "*"];
				else
					queries = ["*" + text + "*"];
				Promise.all(queries.map(q => GraphiteFindMetrics(q).catch(() => [])))
					.then(resultSets =>
					{
						if (counter !== this.searchCounter)
							return; // A newer search superseded this one.
						let seen = {};
						let merged = [];
						for (let set of resultSets)
							for (let r of set)
								if (!seen[r.id])
								{
									seen[r.id] = true;
									merged.push({ id: r.id, text: r.text, leaf: !!r.leaf });
								}
						merged.sort((a, b) => a.id.localeCompare(b.id));
						this.searchTruncated = merged.length > 400;
						this.searchResults = merged.slice(0, 400);
						this.searching = false;
					})
					.catch(err =>
					{
						if (counter !== this.searchCounter)
							return;
						this.searchError = err.message;
						this.searching = false;
					});
			},
			testPath()
			{
				if (!this.selectedPath)
					return;
				this.matchInfo = "counting…";
				GraphiteFindMetrics(this.browseablePath(this.selectedPath))
					.then(results =>
					{
						let leaves = results.filter(r => r.leaf).length;
						let branches = results.length - leaves;
						let parts = [];
						if (leaves)
							parts.push(leaves + " metric" + (leaves === 1 ? "" : "s"));
						if (branches)
							parts.push(branches + " branch" + (branches === 1 ? "" : "es"));
						this.matchInfo = parts.length ? "Matches " + parts.join(" and ") : "No matches";
					})
					.catch(err => { this.matchInfo = "Lookup failed: " + err.message; });
			}
		}
	}
</script>

<style scoped>
	.metricBrowserRoot
	{
		display: flex;
		flex-direction: column;
		width: 700px;
		max-width: calc(100vw - 40px);
		height: 75vh;
		padding: 12px;
		box-sizing: border-box;
	}

	.title
	{
		border-bottom: 1px solid #000000;
		margin-bottom: 10px;
		font-size: 20px;
	}

	.searchRow
	{
		display: flex;
		margin-bottom: 4px;
	}

	.searchInput
	{
		flex: 1 1 auto;
		font-size: 14px;
		padding: 4px 6px;
	}

	.searchHint
	{
		font-size: 12px;
		color: #555555;
		margin-bottom: 4px;
	}

	.browserBody
	{
		flex: 1 1 auto;
		overflow: auto;
		border: 1px solid #BBBBBB;
		background-color: #FAFAFA;
		font-family: Consolas, monospace;
		font-size: 13px;
		padding: 4px;
		margin-bottom: 8px;
	}

	.metricRow
	{
		display: flex;
		align-items: center;
		white-space: nowrap;
		padding: 1px 2px;
	}

		.metricRow.selected
		{
			background-color: #cfe8ff;
		}

	.expander
	{
		flex: 0 0 auto;
		width: 18px;
		text-align: center;
		cursor: pointer;
		color: #444444;
		font-size: 10px;
		user-select: none;
	}

	.rowIcon
	{
		flex: 0 0 auto;
		width: 18px;
		text-align: center;
		color: #888888;
	}

	.rowName
	{
		cursor: pointer;
		color: #000000;
	}

		.rowName:hover
		{
			background-color: rgba(0,0,0,0.07);
		}

	.drillLink
	{
		margin-left: 8px;
		color: #1e78af;
		cursor: pointer;
		font-size: 11px;
	}

	.nodeLoading
	{
		margin-left: 8px;
		color: #888888;
		font-size: 11px;
	}

	.loading, .noResults
	{
		text-align: center;
		margin: 20px 0px;
		font-family: Arial, sans-serif;
	}

	.error
	{
		color: #FF0000;
		font-weight: bold;
		font-family: Arial, sans-serif;
	}

	.pathRow
	{
		display: flex;
		align-items: center;
		gap: 6px;
		margin-bottom: 6px;
	}

	.pathLabel
	{
		flex: 0 0 auto;
	}

	.pathInput
	{
		flex: 1 1 auto;
		font-family: Consolas, monospace;
		font-size: 13px;
		padding: 3px 5px;
	}

	.matchInfo
	{
		flex: 0 0 auto;
		font-size: 12px;
		color: #333333;
		min-width: 90px;
	}

	.templateRow
	{
		font-size: 13px;
		margin-bottom: 6px;
	}

	.buttonRow
	{
		text-align: right;
	}

		.buttonRow input[type=button]
		{
			margin-left: 8px;
		}
</style>
